import { useState, useEffect, useRef } from "react";
import styles from "../styles/theme";
import { UXO_ROLE_OPTIONS } from "../utils/constants";
import { isExpired, isExpiringSoon } from "../utils/helpers";
import { supabase } from "../supabaseClient";

function YesNoBlock({ label, value, onYes, onNo, children }) {
  return (
    <div style={styles.qualBlock}>
      <div style={styles.qualRow}>
        <span style={styles.qualBlockLabel}>{label}</span>
        <div style={styles.yesNoRow}>
          <button type="button" style={{ ...styles.yesNoBtn, ...(value ? styles.yesNoBtnActive : {}) }} onClick={onYes}>Yes</button>
          <button type="button" style={{ ...styles.yesNoBtn, ...(!value ? styles.yesNoBtnNo : {}) }} onClick={onNo}>No</button>
        </div>
      </div>
      {children}
    </div>
  );
}

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ACCEPTED_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
const ACCEPTED_EXT = '.pdf,.jpg,.jpeg,.png';

function InlineDocUpload({ docType, label, existingDoc, uploadState, onUpload }) {
  const fileRef = useRef(null);

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{
        background: '#111316', border: '1px solid #2a2c2e', borderRadius: 6,
        padding: '10px 14px', display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', flexWrap: 'wrap', gap: 8,
      }}>
        <div style={{ flex: 1, minWidth: 140 }}>
          <div style={{ fontSize: 12, color: '#8a8580' }}>{label}</div>
          {existingDoc && (
            <div style={{ fontSize: 11, color: '#4ade80', marginTop: 2 }}>
              {"\u2713"} {existingDoc.file_name}
              <span style={{ color: '#7a7570', marginLeft: 6 }}>{new Date(existingDoc.uploaded_at).toLocaleDateString()}</span>
            </div>
          )}
          {uploadState?.success && !existingDoc && (
            <div style={{ fontSize: 11, color: '#4ade80', marginTop: 2 }}>
              {"\u2713"} {uploadState.fileName}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploadState?.uploading}
            style={{
              background: existingDoc ? 'none' : '#d97706',
              border: existingDoc ? '1px solid #d9770666' : 'none',
              color: existingDoc ? '#d97706' : '#0d0f10',
              padding: '4px 10px', borderRadius: 6, cursor: 'pointer',
              fontSize: 11, fontWeight: existingDoc ? 'normal' : 'bold',
              fontFamily: 'inherit', whiteSpace: 'nowrap',
            }}
          >
            {uploadState?.uploading ? 'Uploading...' : existingDoc ? 'Replace' : 'Upload'}
          </button>
          <input ref={fileRef} type="file" accept={ACCEPTED_EXT} style={{ display: 'none' }}
            onChange={e => { onUpload(e, docType); }} />
        </div>
      </div>
      {uploadState?.error && (
        <div style={{ color: '#ef4444', fontSize: 11, marginTop: 4 }}>{"\u274C"} {uploadState.error}</div>
      )}
    </div>
  );
}


export default function ProfileStep2({
  profile, setProfile, errors, setErrors,
  user, setProfileStep,
}) {
  const [docUploads, setDocUploads] = useState({});
  const [existingDocs, setExistingDocs] = useState([]);
  const [hasProfile, setHasProfile] = useState(null); // null=loading, true/false

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data: prof } = await supabase.from('tech_profiles').select('id').eq('user_id', user.id).single();
      if (!prof) { setHasProfile(false); return; }
      setHasProfile(true);
      const { data: docs } = await supabase.from('tech_documents').select('*').eq('tech_profile_id', prof.id);
      if (docs) setExistingDocs(docs);
    })();
  }, [user]);

  const getExistingDoc = (docType) => existingDocs.find(d => d.document_type === docType);

  const handleDocUpload = async (e, docType) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;

    if (!ACCEPTED_TYPES.includes(file.type)) {
      setDocUploads(prev => ({ ...prev, [docType]: { error: 'Only PDF, JPG, and PNG files are allowed.' } }));
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setDocUploads(prev => ({ ...prev, [docType]: { error: 'File must be under 10MB.' } }));
      return;
    }

    setDocUploads(prev => ({ ...prev, [docType]: { uploading: true } }));

    const storagePath = `${user.id}/docs/${docType}/${Date.now()}_${file.name}`;
    const { error: storageError } = await supabase.storage.from('uxo-uploads').upload(storagePath, file, { upsert: true });
    if (storageError) {
      setDocUploads(prev => ({ ...prev, [docType]: { error: 'Upload failed. Please try again.' } }));
      return;
    }

    const { data: prof } = await supabase.from('tech_profiles')
      .select('id, hazwoper_8_date, physical_date, first_aid_cpr_date')
      .eq('user_id', user.id).single();
    if (!prof) {
      setDocUploads(prev => ({ ...prev, [docType]: { error: 'Profile not found. Save your profile first.' } }));
      return;
    }

    let expiresAt = null;
    const addYears = (dateStr, years) => {
      if (!dateStr) return null;
      const d = new Date(dateStr);
      d.setFullYear(d.getFullYear() + years);
      return d.toISOString().split('T')[0];
    };
    if (docType === 'hazwoper_8hr') expiresAt = addYears(prof.hazwoper_8_date, 1);
    else if (docType === 'physical') expiresAt = addYears(prof.physical_date, 1);
    else if (docType === 'first_aid_cpr') expiresAt = addYears(prof.first_aid_cpr_date, 2);

    const existing = getExistingDoc(docType);
    if (existing) {
      await supabase.storage.from('uxo-uploads').remove([existing.file_path]);
    }

    const { data: upserted, error: dbError } = await supabase.from('tech_documents')
      .upsert({
        tech_profile_id: prof.id, user_id: user.id, document_type: docType,
        file_path: storagePath, file_name: file.name, file_size: file.size,
        uploaded_at: new Date().toISOString(), expires_at: expiresAt,
      }, { onConflict: 'tech_profile_id,document_type' })
      .select().single();

    if (dbError) {
      setDocUploads(prev => ({ ...prev, [docType]: { error: 'Failed to save document record.' } }));
      return;
    }

    setExistingDocs(prev => [...prev.filter(d => d.document_type !== docType), upserted]);
    setDocUploads(prev => ({ ...prev, [docType]: { fileName: file.name, success: true } }));
  };

  const toggleDodCert = (cert) =>
    setProfile(p => ({
      ...p, dodCerts: p.dodCerts.includes(cert) ? p.dodCerts.filter(c => c !== cert) : [...p.dodCerts, cert],
    }));

  const validateStep2 = () => {
    const newErrors = {};
    if (profile.hazwoper8 && isExpired(profile.hazwoper8Date)) newErrors.hazwoper8Date = "Your HAZWOPER 8-Hour Refresher is not current. Please select No.";
    if (profile.physicalCurrent && isExpired(profile.physicalDate)) newErrors.physicalDate = "Your physical is not current. Please select No.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  return (
    <div style={styles.formFields} data-form-fields>

      <div style={{ fontSize: 12, color: '#7a7570', lineHeight: 1.5 }}>
        Documents uploaded in My Documents are visible to employers who unlock your profile.
      </div>

      <label style={styles.label}>UXO Role / Certification Level (select all that apply)</label>
      <div style={styles.certGrid}>
        {UXO_ROLE_OPTIONS.map(cert => (
          <div key={cert} style={{ ...styles.certToggle, ...(profile.dodCerts.includes(cert) ? styles.certToggleActive : {}) }} onClick={() => toggleDodCert(cert)}>
            {profile.dodCerts.includes(cert) ? "\u2713 " : ""}{cert}
          </div>
        ))}
      </div>

      {/* UXO/EOD Cert upload — visible when certs selected */}
      {profile.dodCerts.length > 0 && hasProfile && (
        <InlineDocUpload
          docType="uxo_eod_cert" label="Upload UXO/EOD Certification (PDF, JPG, PNG)"
          existingDoc={getExistingDoc('uxo_eod_cert')}
          uploadState={docUploads['uxo_eod_cert']}
          onUpload={handleDocUpload}
        />
      )}

      <YesNoBlock label="HAZWOPER 40-Hour" value={profile.hazwoper40}
        onYes={() => setProfile(p => ({ ...p, hazwoper40: true }))}
        onNo={() => setProfile(p => ({ ...p, hazwoper40: false, hazwoper40Date: "", hazwoper8: false, hazwoper8Date: "" }))}>
        {profile.hazwoper40 && (
          <div style={styles.subField}>
            <label style={styles.label}>Issue Date</label>
            <input type="date" style={styles.input} value={profile.hazwoper40Date} onChange={e => setProfile(p => ({ ...p, hazwoper40Date: e.target.value }))} />
            <div style={{ fontSize: 11, color: '#7a7570', marginTop: 6 }}>Upload your certificate in the Documents tab after completing your profile</div>
          </div>
        )}
      </YesNoBlock>

      <YesNoBlock label="HAZWOPER 8-Hour Refresher" value={profile.hazwoper8}
        onYes={() => setProfile(p => ({ ...p, hazwoper8: true }))}
        onNo={() => setProfile(p => ({ ...p, hazwoper8: false, hazwoper8Date: "" }))}>
        {profile.hazwoper8 && (
          <div style={styles.subField}>
            <label style={styles.label}>Issue Date</label>
            <input type="date" style={styles.input} value={profile.hazwoper8Date} onChange={e => setProfile(p => ({ ...p, hazwoper8Date: e.target.value }))} />
            {errors.hazwoper8Date && <div style={styles.errorMsg}>{"\u26A0\uFE0F"} {errors.hazwoper8Date}</div>}
            {isExpiringSoon(profile.hazwoper8Date) && !isExpired(profile.hazwoper8Date) && <div style={styles.warnMsg}>{"\u26A0\uFE0F"} Your HAZWOPER 8-Hour Refresher expires within 30 days.</div>}
            <div style={{ fontSize: 11, color: '#7a7570', marginTop: 6 }}>Upload your certificate in the Documents tab after completing your profile</div>
          </div>
        )}
      </YesNoBlock>

      {['UXO Technician III', 'UXO Quality Control', 'UXO Safety Officer / UXOSO', 'SUXOS - Senior UXO Supervisor'].some(r => profile.dodCerts.includes(r)) && (
        <YesNoBlock label="HAZWOPER 8-Hour Supervisor" value={profile.hazwoper8Supervisor}
          onYes={() => setProfile(p => ({ ...p, hazwoper8Supervisor: true }))}
          onNo={() => setProfile(p => ({ ...p, hazwoper8Supervisor: false }))}>
        </YesNoBlock>
      )}

      <YesNoBlock label="10-Hour OSHA Construction Safety" value={profile.osha10Construction}
        onYes={() => setProfile(p => ({ ...p, osha10Construction: true }))}
        onNo={() => setProfile(p => ({ ...p, osha10Construction: false }))}>
        <div style={{ fontSize: 12, color: '#7a7570', marginTop: -2, lineHeight: 1.5 }}>
          Required for UXOSO and SUXOS positions. Includes DOL Construction Safety Course Completion Card.
        </div>
      </YesNoBlock>

      <YesNoBlock label="Current Physical (within 1 year)" value={profile.physicalCurrent}
        onYes={() => setProfile(p => ({ ...p, physicalCurrent: true }))}
        onNo={() => setProfile(p => ({ ...p, physicalCurrent: false, physicalDate: "" }))}>
        {profile.physicalCurrent && (
          <div style={styles.subField}>
            <label style={styles.label}>Issue Date</label>
            <input type="date" style={styles.input} value={profile.physicalDate} onChange={e => setProfile(p => ({ ...p, physicalDate: e.target.value }))} />
            {errors.physicalDate && <div style={styles.errorMsg}>{"\u26A0\uFE0F"} {errors.physicalDate}</div>}
            {isExpiringSoon(profile.physicalDate) && !isExpired(profile.physicalDate) && <div style={styles.warnMsg}>{"\u26A0\uFE0F"} Your physical expires within 30 days.</div>}
            <div style={{ fontSize: 11, color: '#7a7570', marginTop: 6 }}>Upload your certificate in the Documents tab after completing your profile</div>
          </div>
        )}
      </YesNoBlock>

      <YesNoBlock label="First Aid / CPR" value={profile.firstAidCpr}
        onYes={() => setProfile(p => ({ ...p, firstAidCpr: true }))}
        onNo={() => setProfile(p => ({ ...p, firstAidCpr: false, firstAidCprDate: "" }))}>
        <div style={{ fontSize: 12, color: '#7a7570', marginTop: -2 }}>If your First Aid/CPR certification is expired, select No</div>
        {profile.firstAidCpr && (
          <div style={styles.subField}>
            <label style={styles.label}>Issue Date</label>
            <input type="date" style={styles.input} value={profile.firstAidCprDate} onChange={e => setProfile(p => ({ ...p, firstAidCprDate: e.target.value }))} />
            <div style={{ fontSize: 11, color: '#7a7570', marginTop: 6 }}>Upload your certificate in the Documents tab after completing your profile</div>
          </div>
        )}
      </YesNoBlock>

      <YesNoBlock label="Do you have a signed logbook?" value={profile.hasLogbook}
        onYes={() => setProfile(p => ({ ...p, hasLogbook: true }))}
        onNo={() => setProfile(p => ({ ...p, hasLogbook: false }))}>
        {profile.hasLogbook && (
          <div style={{ fontSize: 12, color: '#d97706', marginTop: 4, lineHeight: 1.5 }}>
            Upload your logbook in My Documents to have your hours documented by UXOHire
          </div>
        )}
      </YesNoBlock>

      {/* Quality Credential — shown when UXOQCS or SUXOS is selected */}
      {['UXO Quality Control', 'SUXOS - Senior UXO Supervisor'].some(r => profile.dodCerts.includes(r)) && (
        <div style={{ background: '#0d0f10', border: '1px solid #2a2c2e', borderRadius: 8, padding: '14px 16px' }}>
          <label style={styles.label}>Quality Credential</label>
          <div style={{ fontSize: 12, color: '#7a7570', marginBottom: 8, lineHeight: 1.5 }}>
            Required for UXOQCS and SUXOS positions per DDESB TP-18.
          </div>
          <select style={styles.input} value={profile.qualityCredential || ''} onChange={e => setProfile(p => ({ ...p, qualityCredential: e.target.value }))}>
            <option value="">None</option>
            <option value="ISO 9001 Internal Auditor">ISO 9001 Internal Auditor</option>
            <option value="ASQ Certified Quality Auditor">ASQ Certified Quality Auditor</option>
            <option value="USACE/NAVFAC Construction Quality Management">USACE/NAVFAC Construction Quality Management</option>
            <option value="Company/project-specific QC training">Company/project-specific QC training</option>
          </select>
          {profile.qualityCredential && profile.qualityCredential !== '' && (
            <div style={{ fontSize: 12, color: '#d97706', marginTop: 8, lineHeight: 1.5 }}>
              Upload your quality credential certificate in My Documents to document this qualification.
            </div>
          )}
        </div>
      )}

      {[
        ["EOD Background", "militaryEod"],
        ["Security Clearance", "clearance"],
        ["Dive Certified", "diveCert"],
        ["Driver's License", "driversLicense"],
        ["CDL", "cdl"],
      ].map(([label, key]) => (
        <YesNoBlock key={key} label={label} value={profile[key]}
          onYes={() => setProfile(p => ({ ...p, [key]: true }))}
          onNo={() => setProfile(p => ({ ...p, [key]: false, ...(key === 'clearance' ? { clearanceLevel: "" } : {}) }))}>
          {key === 'clearance' && profile.clearance && (
            <div style={styles.subField}>
              <label style={styles.label}>Clearance Level</label>
              <input style={styles.input} placeholder="e.g. Secret, TS, TS/SCI" value={profile.clearanceLevel} onChange={e => setProfile(p => ({ ...p, clearanceLevel: e.target.value }))} />
            </div>
          )}
        </YesNoBlock>
      ))}

      <div style={{ background: '#0d0f10', border: '1px solid #2a2c2e', borderRadius: 8, padding: '14px 16px' }}>
        <label style={styles.label}>Phone Number (optional)</label>
        <div style={{ fontSize: 12, color: '#7a7570', marginBottom: 8 }}>Visible to employers only after they unlock your profile.</div>
        <input style={styles.input} type="tel" placeholder="(555) 123-4567" value={profile.phone || ''} onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))} />
      </div>

      {/* ── Hour documentation info callout ── */}
      <div style={{
        background: '#1a1408', border: '1px solid #d9770644', borderRadius: 8,
        padding: '14px 16px', lineHeight: 1.6,
      }}>
        <div style={{ fontSize: 14, fontWeight: 'bold', color: '#d97706', marginBottom: 4 }}>
          Get your hours documented
        </div>
        <div style={{ fontSize: 13, color: '#b0aca6' }}>
          Upload your signed logbook after completing your profile to earn an Hours Documented badge visible to employers. Documented hours give you a competitive advantage.
        </div>
        {/* Set the expectation up front: the logbook is the source of truth, and a
            profile figure above it gets documented down, not up. */}
        <div style={{ fontSize: 12, color: '#7a7570', marginTop: 8 }}>
          We document hours based on your logbook. If your logbook shows more hours than your profile,
          we'll document the lower number. Make sure your profile hours match your logbook before submitting.
        </div>
      </div>

      {/* ── Resume Upload (edit mode) or note (new profile) ── */}
      {hasProfile ? (
        <div style={{
          background: '#111316', border: '1px solid #2a2c2e', borderRadius: 10,
          padding: '16px 18px',
        }}>
          <div style={{ fontSize: 14, fontWeight: 'bold', color: '#e8e4dc', marginBottom: 8 }}>Resume</div>
          <InlineDocUpload
            docType="resume" label="Upload Resume (PDF, JPG, PNG)"
            existingDoc={getExistingDoc('resume')}
            uploadState={docUploads['resume']}
            onUpload={handleDocUpload}
          />
          <div style={{ fontSize: 11, color: '#555', marginTop: 10 }}>
            Accepted formats: PDF, JPG, PNG. Max 10MB per file.
          </div>
        </div>
      ) : hasProfile === false ? (
        <div style={{
          background: '#0a1a2a', border: '1px solid #60a5fa33', borderRadius: 8,
          padding: '14px 16px', fontSize: 13, color: '#60a5fa', lineHeight: 1.6,
        }}>
          You can upload your certifications and resume after completing your profile. Finish creating your profile, then visit the My Documents tab on your dashboard.
        </div>
      ) : null}

      <div style={styles.formRow} data-form-row>
        <button type="button" style={styles.btnSecondary} data-btn-secondary onClick={() => setProfileStep(1)}>{"\u2190"} Back</button>
        <button type="button" style={styles.btnPrimary} data-btn-primary onClick={() => { if (validateStep2()) setProfileStep(3); }}>Next {"\u2192"}</button>
      </div>
    </div>
  );
}
